/**
 * Visit Tracker - 真實用戶訪問追蹤系統
 * 收集用戶行為數據並檢測bot/automated訪問
 */

class VisitTracker {
  constructor() {
    this.startTime = Date.now();
    this.fingerprint = null;
    this.interactions = [];
    this.scrollDepth = 0;
    this.isTracking = false;
    this.teacherSlug = null;
    this.referrerTeacher = null;
    this.referrerStudent = null;  // v3.3: 學生推薦來源

    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleUserInteraction = this.handleUserInteraction.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  /**
   * 初始化訪問追蹤
   */
  initialize(teacherSlug, referrerTeacher = null, teacherId = null, metadata = {}) {
    this.teacherSlug = teacherSlug;
    this.referrerTeacher = referrerTeacher;
    this.teacherId = teacherId;
    const { referrerUrl, pageUrl, referrerStudent } = metadata || {};  // v3.3: 加入 referrerStudent
    this.referrerStudent = referrerStudent || null;  // v3.3: 學生推薦來源
    const currentLocation = window?.location?.href || null;
    const documentReferrer = document?.referrer || null;
    this.pageUrl = pageUrl || currentLocation;
    this.referrerUrl = typeof referrerUrl === 'string' ? referrerUrl : documentReferrer;

    this.generateFingerprint();
    this.setupEventListeners();
    this.isTracking = true;

  }

  /**
   * 生成瀏覽器指紋
   */
  generateFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint test', 2, 2);
    
    const fingerprints = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      typeof(Worker),
      canvas.toDataURL()
    ];

    // 簡單hash函數
    let hash = 0;
    const str = fingerprints.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    this.fingerprint = Math.abs(hash).toString(16);
  }

  /**
   * 設置事件監聽器
   */
  setupEventListeners() {
    // 頁面可見性變化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // 頁面卸載前
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // 用戶互動事件
    ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'].forEach(event => {
      document.addEventListener(event, this.handleUserInteraction, { passive: true });
    });

    // 滾動深度追蹤
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  /**
   * 處理頁面可見性變化
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // 頁面隱藏時記錄
      this.addInteraction('page_hidden');
    } else {
      // 頁面顯示時記錄
      this.addInteraction('page_visible');
    }
  }

  /**
   * 處理頁面卸載前
   */
  handleBeforeUnload() {
    if (this.isTracking) {
      this.sendVisitData(true); // 同步發送
    }
  }

  /**
   * 處理用戶互動
   */
  handleUserInteraction(event) {
    if (!this.isTracking) return;

    this.addInteraction(event.type, {
      timestamp: Date.now() - this.startTime,
      target: event.target.tagName,
      x: event.clientX,
      y: event.clientY
    });

    // 限制記錄的互動數量
    if (this.interactions.length > 50) {
      this.interactions = this.interactions.slice(-30);
    }
  }

  /**
   * 處理滾動事件
   */
  handleScroll() {
    if (!this.isTracking) return;

    const scrollTop = window.pageYOffset;
    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const scrollPercent = Math.round((scrollTop / (docHeight - winHeight)) * 100);

    if (scrollPercent > this.scrollDepth) {
      this.scrollDepth = scrollPercent;
    }
  }

  /**
   * 添加互動記錄
   */
  addInteraction(type, data = {}) {
    this.interactions.push({
      type,
      timestamp: Date.now() - this.startTime,
      ...data
    });
  }

  /**
   * 計算訪問時長
   */
  getVisitDuration() {
    return Math.round((Date.now() - this.startTime) / 1000);
  }

  /**
   * 檢查是否為真實用戶行為
   */
  isRealUserBehavior() {
    const duration = this.getVisitDuration();
    const interactionCount = this.interactions.length;

    // 基本檢查
    if (duration < 1) return false;
    if (interactionCount === 0 && duration < 10) return false;

    // 檢查互動類型多樣性
    const interactionTypes = new Set(this.interactions.map(i => i.type));
    const hasMouseMove = interactionTypes.has('mousemove');
    const hasClick = interactionTypes.has('click');
    const hasScroll = interactionTypes.has('scroll');

    // 有意義的互動才算真實用戶
    return (hasMouseMove && duration > 3) || hasClick || hasScroll || this.scrollDepth > 10;
  }

  /**
   * 獲取用戶行為特徵
   */
  getBehaviorSignature() {
    const interactionTypes = this.interactions.reduce((acc, interaction) => {
      acc[interaction.type] = (acc[interaction.type] || 0) + 1;
      return acc;
    }, {});

    return {
      totalInteractions: this.interactions.length,
      interactionTypes,
      scrollDepth: this.scrollDepth,
      avgTimeBetweenInteractions: this.calculateAvgTimeBetweenInteractions(),
      visitDuration: this.getVisitDuration(),
      isRealUser: this.isRealUserBehavior()
    };
  }

  /**
   * 計算互動間平均時間
   */
  calculateAvgTimeBetweenInteractions() {
    if (this.interactions.length < 2) return 0;

    const timeDiffs = [];
    for (let i = 1; i < this.interactions.length; i++) {
      timeDiffs.push(this.interactions[i].timestamp - this.interactions[i-1].timestamp);
    }

    return timeDiffs.reduce((sum, diff) => sum + diff, 0) / timeDiffs.length;
  }

  /**
   * 發送訪問數據到服務器
   */
  async sendVisitData(isSync = false) {
    if (!this.teacherId || !this.fingerprint) return;

    const visitData = {
      teacher_id: this.teacherId,
      ref: this.referrerTeacher,
      ref_student: this.referrerStudent,  // v3.3: 學生推薦來源
      visit_action: 'start',
      user_agent: navigator.userAgent,
      fingerprint: this.fingerprint,
      visit_duration: this.getVisitDuration(),
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      color_depth: screen.colorDepth.toString(),
      pixel_ratio: window.devicePixelRatio.toString(),
      timestamp: Math.floor(Date.now() / 1000),
      page_url: this.pageUrl || '',
      referrer_url: this.referrerUrl || ''
    };

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: new URLSearchParams(visitData).toString()
    };

    try {
      if (isSync && navigator.sendBeacon) {
        // 使用sendBeacon進行同步發送（頁面卸載時）
        const formData = new FormData();
        Object.keys(visitData).forEach(key => {
          formData.append(key, visitData[key]);
        });
        formData.append('authenticity_token', this.getCSRFToken());
        
        navigator.sendBeacon('/api/visits/track', formData);
      } else {
        // 異步發送
        const response = await fetch('/api/visits/track', options);
        if (response.ok) {
          this.isTracking = false;
        }
      }
    } catch (error) {
      console.error('Failed to send visit data:', error);
    }
  }

  /**
   * 獲取CSRF token
   */
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute('content') : '';
  }

  /**
   * 定期發送訪問數據（對於長時間訪問）
   */
  startPeriodicReporting() {
    // 每30秒發送一次數據（對於長時間訪問）
    setInterval(() => {
      if (this.isTracking && this.getVisitDuration() > 30) {
        this.sendVisitData();
      }
    }, 30000);
  }

  /**
   * 清理事件監聽器
   */
  cleanup() {
    this.isTracking = false;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    
    ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'].forEach(event => {
      document.removeEventListener(event, this.handleUserInteraction);
    });
    
    window.removeEventListener('scroll', this.handleScroll);
  }
}

// Export for use in other modules
export default VisitTracker;
